import { EditorSdkAdapter } from '@wix/bookings-adapter-editor-sdk';
import {
  BookingsAppBuilderStaticsUrls,
  DAILY_TIMETABLE_WIDGET_DEV_CENTER_ID,
  STAFF_LIST_WIDGET_DEV_CENTER_ID,
} from '@wix/bookings-app-builder-controllers/dist/src/platform/platform.const';
import {
  BOOKINGS_APP_DEF_ID,
  ECOM_APP_DEF_ID,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import {
  EcomComponentConfiguration,
  withEcomPlatformData,
} from '@wix/ecom-platform-sdk';
import { MA_APP_IDS, withMembersArea } from '@wix/members-area-integration-kit';
import { IFedOpsLogger } from '@wix/native-components-infra/dist/src/types/types';
import {
  EditorReadyOptions,
  EditorSDK,
  EventType,
} from '@wix/platform-editor-sdk';
import { WidgetPluginInterfaces } from '@wix/widget-plugins-ooi/interfaces';
import { EditorScriptApi } from './api/api';
import {
  BOOK_BUTTON_WIDGET_CONTROLLER_ID,
  DAILY_TIMETABLE_WIDGET_CONTROLLER_ID,
  experiments,
  HandleActionPayload,
  PageId,
  STAFF_LIST_WIDGET_CONTROLLER_ID,
  WidgetsId,
} from './constants';
import { BookButtonEditorComponentModel } from './editor-components/book-button';
import { DailyTimetableEditorComponentModel } from './editor-components/daily-timetable';
import { StaffListEditorComponentModel } from './editor-components/staff-list';
import createAppDescriptor from './manifest/app-descriptor/app-descriptor';
import { migrateAction } from './migration';
import { EditorViewStates, SlotIds } from './types';
import { handleOpenBookingsAddPanel } from './utils/app-descriptor-actions';
import {
  openBookingsDashboardAddNewService,
  openManageBookings,
  openBackOfficeCalendar,
  openFormFieldSettingsPanel,
  openBookingsAvailability,
  openBookingsSettings,
  openSharableLinks,
  openIntegrations,
  openManageClientFlow,
} from './utils/backoffice-actions';
import { getCurrentVersion } from './utils/ci-actions';
import {
  installCart,
  requiredEcomComponentsAction,
} from './utils/ecom-migration/ecom-actions';
import { validateInstallationCompleted } from './utils/complete-installation/validate-installation-completed';
import { EditorActionEvents, generateActions } from './utils/editor-actions';
import {
  addEditorActionsListeners,
  EditorActions,
} from './utils/editor-actions-events';
import {
  Actions,
  BI_COMPONENT_APP_ACTION,
  getErrorInfo,
  reportBi,
  reportEditorAction,
} from './utils/editor-script-bi-logger';
import {
  createFedopsLogger,
  getAllBookingsPages,
  getPageData,
  getStateBoxByBookingsAppBuilderWidget,
  isADI,
  isBookingCalendarInstalled,
  isBookingFormInstalled,
  isBookingsCheckoutInstalled,
  isServicePageInstalled,
  onRemoveApp,
  removePage,
  getAllSiteComponents,
  getFullComponentStructure,
  setBooleanStyleParam,
} from './utils/editor-sdk-actions';
import { isExperimentEnabled } from './utils/experiments';
import {
  addHistoryEventsHandlers,
  onHistoryEvent,
} from './utils/history-events';
import { shouldProposeMigration } from './utils/migrate-actions';
import { migrator, proposeListMigration } from './utils/migration-modals';
import {
  addBookingsPagesAsPanel,
  createBookCheckoutState,
  createBookingCalendarState,
  createBookingFormState,
  createServicePageState,
  handleOpenBookingsPagesPanel,
  removeBookCheckoutPageOnEcom,
  updatePagesTranslations,
} from './utils/pages-panel-actions';
import { Translations } from './utils/translations';

export enum EditorEventTypes {
  DELETE_BOOKINGS = 'deleteBookings',
  DELETE_PAGE = 'deletePage',
  PAGE_CHANGED = 'focusedPageChanged',
  WIDGET_GFPP_CLICKED = 'widgetGfppClicked',
  COMPONENT_GFPP_CLICKED = 'componentGfppClicked',
  COMPONENT_STYLE_CHANGED = 'componentStyleChanged',
  GFPP_ORIGIN_DOUBLE_CLICK = 'double_click',
  APP_ACTION_CLICKED = 'appActionClicked',
  SET_YOUR_AVAILABILITY = 'set_your_availability',
  EDIT_FORM_FIELDS = 'edit-form-fields',
}

const checkIfUserIsPremium = async (
  sdk: EditorSDK,
  appToken: string,
): Promise<boolean> => {
  const vendorProductId = await sdk?.document?.info?.getVendorProductId(
    appToken,
    { appDefinitionId: BOOKINGS_APP_DEF_ID },
  );
  return Boolean(vendorProductId);
};

let requiredEcomComponents: () => Promise<EcomComponentConfiguration> =
  async () => ({ ecomComponents: {} });

export const createEditorScript = (
  editorTranslation: Translations,
  isBookingsOnEcom = false,
) => {
  try {
    let instance,
      appToken,
      sdk,
      locale,
      isAdi,
      isProposeMigrationNeeded,
      allSitePages,
      isFirstInstall,
      msid,
      editorOrigin,
      isBookingCalendarPageInstalled,
      editorScriptApi: EditorScriptApi,
      editorOptions: EditorReadyOptions,
      fedopsLogger: IFedOpsLogger;

    let bookingsEditorComponentModels;
    let actions;
    let appDescriptor;
    let ooiControllersStageData;
    const isEditor3 = globalThis.commonConfig?.host === 'EDITOR3';

    return {
      editorReady: async (
        _editorSDK: EditorSDK,
        _appToken: string,
        options: EditorReadyOptions,
      ) => {
        try {
          fedopsLogger = createFedopsLogger(options);
          fedopsLogger.interactionStarted(Actions.EDITOR_READY);

          if (options?.firstInstall) {
            fedopsLogger.interactionStarted(Actions.BOOKINGS_INSTALLATION);
            fedopsLogger.interactionStarted(Actions.ECOM_INSTALLATION);
          }

          const editorSdkAdapter = new EditorSdkAdapter(_editorSDK, _appToken);
          locale = await editorSdkAdapter.getEditorLanguage();
          const currentVersion = await getCurrentVersion(options);
          await editorTranslation.init(locale, currentVersion);

          msid = await _editorSDK.document.info.getMetaSiteId('');
          editorOrigin = options?.origin?.type;
          isFirstInstall = options?.firstInstall;

          await addHistoryEventsHandlers(_editorSDK);

          editorOptions = options;
          isBookingCalendarPageInstalled = await isBookingCalendarInstalled(
            _editorSDK,
          );

          addEditorActionsListeners(_editorSDK, editorSdkAdapter, _appToken);

          try {
            if (!(await editorSdkAdapter.isEcomInstalled())) {
              fedopsLogger.interactionStarted(Actions.ECOM_INSTALLATION);
            }
          } catch (e) {
            fedopsLogger.interactionStarted(Actions.ECOM_INSTALLATION);
            console.error(e);
          }

          editorScriptApi = new EditorScriptApi(
            options.essentials.httpClient as any,
          );
          instance = options.initialAppData.instance;
          actions = generateActions(editorTranslation);

          const isPremium = await checkIfUserIsPremium(_editorSDK, _appToken);

          appDescriptor = createAppDescriptor(
            actions,
            isPremium,
            editorTranslation,
            currentVersion,
            isEditor3,
          );
          const isAdditionalTimeSlotsInFormPluginEnabled =
            await isExperimentEnabled(
              experiments.ADDITIONAL_TIME_SLOTS_IN_FORM_PLUGIN,
            );

          ooiControllersStageData = {
            [WidgetsId.CALENDAR_PAGE]: {
              default: {
                layoutLimits: {
                  desktop: {
                    minWidth: 280,
                  },
                  mobile: {
                    minWidth: 280,
                  },
                },
                slots: {
                  [SlotIds.CALENDAR_AVAILABILITY]: {
                    displayName: SlotIds.CALENDAR_AVAILABILITY,
                    interfaces: [WidgetPluginInterfaces.BOOKINGS_CALENDAR],
                  },
                },
              },
            },
            [WidgetsId.CALENDAR_WIDGET]: {
              default: {
                layoutLimits: {
                  desktop: {
                    minWidth: 280,
                  },
                  mobile: {
                    minWidth: 280,
                  },
                },
                slots: {
                  [SlotIds.CALENDAR_AVAILABILITY]: {
                    displayName: SlotIds.CALENDAR_AVAILABILITY,
                    interfaces: [WidgetPluginInterfaces.BOOKINGS_CALENDAR],
                  },
                },
              },
            },
            [WidgetsId.WEEKLY_TIMETABLE]: {
              default: {
                layoutLimits: {
                  desktop: {
                    minWidth: 640,
                  },
                  mobile: {
                    minWidth: 280,
                  },
                },
              },
            },
            [WidgetsId.DAILY_AGENDA]: {
              default: {
                layoutLimits: {
                  desktop: {
                    minWidth: 280,
                  },
                  mobile: {
                    minWidth: 280,
                  },
                },
              },
            },
            [WidgetsId.BOOKINGS_LIST_PAGE]: {
              default: {
                layoutLimits: {
                  desktop: {
                    minWidth: 600,
                  },
                  mobile: {
                    minWidth: 260,
                  },
                },
              },
            },
            [WidgetsId.BOOKINGS_LIST_WIDGET]: {
              default: {
                layoutLimits: {
                  desktop: {
                    minWidth: 600,
                  },
                  mobile: {
                    minWidth: 260,
                  },
                },
              },
            },
            [WidgetsId.SERVICE_PAGE]: {
              default: {
                slots: {
                  slot1: {
                    displayName: 'slot1',
                    interfaces: [WidgetPluginInterfaces.BOOKINGS_SERVICE],
                  },
                },
              },
            },
            ...(isAdditionalTimeSlotsInFormPluginEnabled
              ? {
                  [WidgetsId.FORM_PAGE]: {
                    default: {
                      viewStates: {
                        bookingForm: {
                          title: editorTranslation.t(
                            'members-area.form.view-states.checkout',
                          ),
                          initial: true,
                        },
                        addedToCartModal: {
                          title: editorTranslation.t(
                            'members-area.form.view-states.cart-modal',
                          ),
                          initial: false,
                        },
                      },
                      slots: {
                        [SlotIds.ADDED_TO_CART_MODAL]: {
                          displayName: SlotIds.ADDED_TO_CART_MODAL,
                          interfaces: [WidgetPluginInterfaces.BOOKINGS_FORM],
                        },
                      },
                    },
                  },
                }
              : {}),
          };
          bookingsEditorComponentModels = {
            [DAILY_TIMETABLE_WIDGET_DEV_CENTER_ID]:
              new DailyTimetableEditorComponentModel(
                editorSdkAdapter,
                editorTranslation,
                actions.manageBookingsAction,
                actions.manageStateAction,
                actions.openDailyTimeTableSettings,
                actions.openDailyTimeTableSettingsTextTab,
              ),
            [BOOK_BUTTON_WIDGET_CONTROLLER_ID]:
              new BookButtonEditorComponentModel(
                editorSdkAdapter,
                editorTranslation,
                actions.manageBookingsAction,
                actions.bookButtonSettings,
              ),
            [STAFF_LIST_WIDGET_DEV_CENTER_ID]:
              new StaffListEditorComponentModel(
                editorSdkAdapter,
                editorTranslation,
                {
                  manageStaffAction: actions.manageStaffAction,
                  staffListSettingsAction: actions.openStaffListSettings,
                  openStaffListTextSettingsAction:
                    actions.openStaffListTextSettings,
                },
              ),
          };
          await editorSdkAdapter.registerToCustomEvents([
            EditorEventTypes.COMPONENT_STYLE_CHANGED,
          ]);

          fedopsLogger.interactionEnded(Actions.EDITOR_READY);
          return new Promise(async (resolve) => {
            try {
              fedopsLogger.interactionStarted(Actions.EDITOR_READY_CALLBACK);
              appToken = _appToken;
              sdk = _editorSDK;
              isAdi = isADI(options);

              if (isFirstInstall) {
                const setupBookingsBoilerplate = async () => {
                  await addBookingsPagesAsPanel(sdk, appToken);
                };
                try {
                  await sdk.document.transactions.runAndWaitForApproval(
                    appToken,
                    async () => {
                      await setupBookingsBoilerplate();

                      try {
                        const allComponents = await getAllSiteComponents(
                          sdk,
                          appToken,
                        );
                        const siteStructure: any[] = await Promise.all(
                          allComponents.map((componentRef) =>
                            getFullComponentStructure(
                              sdk,
                              appToken,
                              componentRef,
                            ),
                          ),
                        );

                        const breadcrumbPages = siteStructure.filter(
                          (comp) =>
                            (comp?.data?.widgetId ===
                              WidgetsId.BOOKINGS_LIST_PAGE ||
                              comp?.data?.widgetId ===
                                WidgetsId.SERVICE_PAGE) &&
                            comp?.id,
                        );

                        await Promise.all(
                          breadcrumbPages.map((page) =>
                            setBooleanStyleParam(sdk, appToken, page.id, {
                              key: 'showBreadcrumbs',
                              value: true,
                            }),
                          ),
                        );
                      } catch (e) {
                        console.error(
                          '[servicesPagesBreadcrumbs] failed to set showBreadcrumbs on firstInstall',
                          e,
                        );
                      }
                    },
                  );
                } catch (error) {
                  console.error('Concurrent editing problem: ', error);
                }
              } else {
                await sdk.document.application.registerToCustomEvents(
                  appToken,
                  {
                    eventTypes: [EditorEventTypes.PAGE_CHANGED],
                  },
                );
                isProposeMigrationNeeded = shouldProposeMigration(
                  sdk,
                  isAdi,
                  appToken,
                  instance,
                );
              }

              allSitePages = await sdk.pages.data.getAll();
              await createBookCheckoutState(sdk, appToken, allSitePages);
              await createServicePageState(sdk, appToken, allSitePages);
              await createBookingCalendarState(sdk, appToken, allSitePages);
              await createBookingFormState(sdk, appToken, allSitePages);
              await removeBookCheckoutPageOnEcom(sdk, appToken);

              const isBookingTranslatePagesEnabled = await isExperimentEnabled(
                experiments.BOOKINGS_TRANSLATE_PAGES,
              );

              if (isBookingTranslatePagesEnabled) {
                await updatePagesTranslations(
                  sdk,
                  appToken,
                  allSitePages,
                  editorTranslation,
                );
              }

              requiredEcomComponents = requiredEcomComponentsAction(options);

              const isImproveFixSiteEnabled = await isExperimentEnabled(
                experiments.IMPROVE_FIX_SITE,
              );

              await validateInstallationCompleted({
                isFirstInstall,
                _editorSDK,
                msid,
                editorOrigin,
                editorScriptApi,
                appToken: _appToken,
                locale,
                editorTranslation,
                fedopsLogger,
                instance,
                completeInstall: {
                  editorSdk: _editorSDK,
                  appToken: _appToken,
                  editorScriptApi,
                  editorOptions,
                  instance,
                  isBookingsOnEcom,
                  displayStepsBar: {
                    editorTranslation,
                  },
                  // Only include payload when experiment is OFF
                  ...(!isImproveFixSiteEnabled && {
                    payload: {
                      appDefinitionId: BOOKINGS_APP_DEF_ID,
                      OOIMigration: true,
                    },
                  }),
                },
              });

              await installCart({
                editorSdk: _editorSDK,
                editorOptions,
                locale,
                instance,
                msid,
                editorOrigin,
                editorScriptApi,
                fedopsLogger,
              });

              fedopsLogger.interactionEnded(Actions.EDITOR_READY_CALLBACK);
              resolve(undefined);
            } catch (e) {
              const errorMessage = `${
                e?.message ? e.message : JSON.stringify(e)
              } - Action ${Actions.EDITOR_READY_CALLBACK}`;
              reportBi({
                msid,
                origin: editorOrigin,
                isFirstInstall,
                actionType: Actions.EDITOR_READY_CALLBACK,
                actionSucceed: false,
                errorMessage,
              });
              throw new Error(errorMessage);
            }
          });
        } catch (e) {
          const errorMessage = JSON.stringify(getErrorInfo(e));
          console.log(errorMessage);
          reportBi({
            msid,
            origin: editorOrigin,
            isFirstInstall,
            actionType: Actions.EDITOR_READY,
            actionSucceed: false,
            errorMessage,
          });
          throw new Error(errorMessage);
        }
      },
      getAppManifest: async (
        _editorSDK: EditorSDK,
        _appToken: string,
        options: EditorReadyOptions,
      ) => {
        try {
          fedopsLogger.interactionStarted(Actions.GET_APP_MANIFEST);
          const [
            isBookingsCalendarAppReflowEnable,
            isBookingsServicePageAppReflowEnable,
          ] = await Promise.all([
            isExperimentEnabled(experiments.BOOKINGS_CALENDAR_APP_REFLOW),
            isExperimentEnabled(experiments.BOOKINGS_SERVICE_PAGE_APP_REFLOW),
          ]);
          isAdi = isADI(options);

          const result = {
            ...(appDescriptor ? { appDescriptor } : {}),
            controllersStageData: {
              [DAILY_TIMETABLE_WIDGET_CONTROLLER_ID]: {
                default:
                  bookingsEditorComponentModels[
                    DAILY_TIMETABLE_WIDGET_DEV_CENTER_ID
                  ].connection,
              },
              [BOOK_BUTTON_WIDGET_CONTROLLER_ID]: {
                default:
                  bookingsEditorComponentModels[
                    BOOK_BUTTON_WIDGET_CONTROLLER_ID
                  ].connection,
              },
              [STAFF_LIST_WIDGET_CONTROLLER_ID]: {
                default:
                  bookingsEditorComponentModels[STAFF_LIST_WIDGET_DEV_CENTER_ID]
                    .connection,
              },
              [WidgetsId.MEMBERS_BOOKINGS_WIDGET]: {
                default: {
                  behavior: {
                    removable: false,
                    viewStates: {
                      bookings: {
                        title: editorTranslation.t(
                          'members-area.my-bookings.view-states.bookings',
                        ),
                        initial: true,
                      },
                      emptyState: {
                        title: editorTranslation.t(
                          'members-area.my-bookings.view-states.empty-state',
                        ),
                        initial: false,
                      },
                    },
                  },
                  gfpp: {
                    desktop: {
                      iconButtons: { widgetPlugins: 'HIDE' },
                    },
                  },
                },
              },
              ...ooiControllersStageData,
            },
            pages: {
              pageActions: {
                default: ['Pages_Actions_Page_Rename'],
                bookCheckoutPage: [],
                servicePage: [],
                bookingCalendar: [],
                bookingForm: [],
              },
              pageSettings: {
                default: [
                  {
                    title: editorTranslation.t('bookings-pages.tabs.page-info'),
                    helpId: 'c7cfedc0-f0c7-4ea3-9f91-9e1e9a5f7b33',
                    type: 'page_info',
                  },
                  {
                    title: editorTranslation.t('bookings-pages.tabs.layout'),
                    type: 'layout',
                  },
                  {
                    title: editorTranslation.t(
                      'bookings-pages.tabs.permissions',
                    ),
                    type: 'permissions',
                  },
                  {
                    title: editorTranslation.t('bookings-pages.tabs.seo'),
                    type: 'seo',
                  },
                  ...(isEditor3
                    ? [
                        {
                          title: editorTranslation.t(
                            'bookings-pages.tabs.seo-accessibility',
                          ),
                          type: 'a11y',
                        },
                        {
                          title: editorTranslation.t(
                            'bookings-pages.tabs.social-share',
                          ),
                          type: 'social_share',
                        },
                      ]
                    : []),
                ],
                bookCheckoutPage: [
                  {
                    title: editorTranslation.t('bookings-pages.tabs.page-info'),
                    url: `https://bookings.wixapps.net/bookings-widget/book-checkout-page-info?locale=${locale}&instance=${instance}&isBookingCalendarInstalled=${isBookingCalendarPageInstalled}&isEditor3=${isEditor3}`,
                    helpId: '2fd96dc5-ff35-4ead-9917-12b487c59fe4',
                    type: 'page_info',
                  },
                  {
                    title: editorTranslation.t('bookings-pages.tabs.layout'),
                    type: 'layout',
                  },
                  {
                    title: editorTranslation.t(
                      'bookings-pages.tabs.permissions',
                    ),
                    type: 'permissions',
                  },
                ],
                servicePage: [
                  {
                    title: editorTranslation.t('bookings-pages.tabs.page-info'),
                    url: `https://bookings.wixapps.net/bookings-widget/service-page-info?locale=${locale}&instance=${instance}&isEditor3=${isEditor3}`,
                    helpId: '7137bae8-3fe7-41ab-a7f5-80eddb9d9bad',
                    type: 'page_info',
                  },
                  {
                    title: editorTranslation.t('bookings-pages.tabs.layout'),
                    type: 'layout',
                  },
                  {
                    title: editorTranslation.t(
                      'bookings-pages.tabs.permissions',
                    ),
                    type: 'permissions',
                  },
                  ...(isEditor3
                    ? [
                        {
                          title: editorTranslation.t('bookings-pages.tabs.seo'),
                          type: 'seo',
                        },
                        {
                          title: editorTranslation.t(
                            'bookings-pages.tabs.seo-accessibility',
                          ),
                          type: 'a11y',
                        },
                        {
                          title: editorTranslation.t(
                            'bookings-pages.tabs.social-share',
                          ),
                          type: 'social_share',
                        },
                      ]
                    : []),
                ],
                bookingCalendar: [
                  {
                    title: editorTranslation.t('bookings-pages.tabs.page-info'),
                    url: `https://bookings.wixapps.net/bookings-widget/booking-calendar-page-info?locale=${locale}&instance=${instance}&isEditor3=${isEditor3}`,
                    helpId: '7137bae8-3fe7-41ab-a7f5-80eddb9d9bad',
                    type: 'page_info',
                  },
                  {
                    title: editorTranslation.t('bookings-pages.tabs.layout'),
                    type: 'layout',
                  },
                  {
                    title: editorTranslation.t(
                      'bookings-pages.tabs.permissions',
                    ),
                    type: 'permissions',
                  },
                  ...(isEditor3
                    ? [
                        {
                          title: editorTranslation.t(
                            'bookings-pages.tabs.seo-accessibility',
                          ),
                          type: 'a11y',
                        },
                      ]
                    : []),
                ],
                bookingForm: [
                  {
                    title: editorTranslation.t('bookings-pages.tabs.page-info'),
                    url: `https://bookings.wixapps.net/bookings-widget/booking-form-page-info?locale=${locale}&instance=${instance}&isEditor3=${isEditor3}`,
                    helpId: '7137bae8-3fe7-41ab-a7f5-80eddb9d9bad',
                    type: 'page_info',
                  },
                  {
                    title: editorTranslation.t('bookings-pages.tabs.layout'),
                    type: 'layout',
                  },
                  {
                    title: editorTranslation.t(
                      'bookings-pages.tabs.permissions',
                    ),
                    type: 'permissions',
                  },
                  ...(isEditor3
                    ? [
                        {
                          title: editorTranslation.t(
                            'bookings-pages.tabs.seo-accessibility',
                          ),
                          type: 'a11y',
                        },
                      ]
                    : []),
                ],
              },
              applicationSettings: {
                default: {
                  displayName: editorTranslation.t('bookings-pages.title'),
                  helpId: 'c7cfedc0-f0c7-4ea3-9f91-9e1e9a5f7b33',
                  pageReplace: {
                    [PageId.BOOKINGS_CALENDAR_PAGE]:
                      isBookingsCalendarAppReflowEnable && !isAdi
                        ? {
                            slug: '/',
                            replaceable: true,
                            replacerHelpUrl:
                              'https://dev.wix.com/docs/develop-websites/articles/wix-apps/wix-bookings/build-a-custom-booking-calendar-page',
                          }
                        : { replaceable: false },
                    [PageId.BOOKINGS_SERVICE_PAGE]:
                      isBookingsServicePageAppReflowEnable && !isAdi
                        ? {
                            slug: '/',
                            replaceable: true,
                            replacerHelpUrl:
                              'https://dev.wix.com/docs/develop-websites/articles/wix-apps/wix-bookings/build-a-custom-booking-service-page',
                          }
                        : { replaceable: false },
                  },
                },
              },
              applicationActions: {
                default: {
                  defaultValues: [
                    {
                      title: editorTranslation.t(
                        'bookings-pages.actions.manage',
                      ),
                      event: EditorActionEvents.MANAGE_BOOKINGS,
                      icon: 'settingsAction',
                    },
                    {
                      title: editorTranslation.t(
                        'bookings-pages.actions.delete',
                      ),
                      event: EditorEventTypes.DELETE_BOOKINGS,
                      icon: 'deleteRadio',
                    },
                  ],
                },
              },
              pageDescriptors: {
                default: {
                  icon: 'bookingPageType',
                  orderIndex: 5,
                },
                servicePage: {
                  icon: 'bookingPageType',
                  orderIndex: 4,
                },
                bookingCalendar: {
                  icon: 'bookingPageType',
                  orderIndex: 3,
                },
                bookingForm: {
                  icon: 'bookingPageType',
                  orderIndex: 2,
                },
                bookCheckoutPage: {
                  icon: 'bookingPageType',
                  orderIndex: 1,
                },
              },
            },
          };
          fedopsLogger.interactionEnded(Actions.GET_APP_MANIFEST);
          return result;
        } catch (e) {
          const errorMessage = `${
            e?.message ? e.message : JSON.stringify(e)
          } - Action ${Actions.GET_APP_MANIFEST}`;
          reportBi({
            msid,
            origin: editorOrigin,
            isFirstInstall,
            actionType: Actions.GET_APP_MANIFEST,
            actionSucceed: false,
            errorMessage,
          });
          throw new Error(errorMessage);
        }
      },
      onEvent: async (event, editorSDK) => {
        fedopsLogger.interactionStarted(Actions.ON_EVENT);

        const { eventType, eventPayload } = event;
        const editorSdkAdapter = new EditorSdkAdapter(editorSDK, appToken);
        onHistoryEvent(event);

        async function handleWidgetPluginAdded() {
          try {
            const { placement, hostComponentRef } = event.eventPayload;

            if (placement.slotId === SlotIds.ADDED_TO_CART_MODAL) {
              await editorSDK.editor.widgets.setViewState('', {
                componentRef: hostComponentRef,
                viewState: EditorViewStates.ADDED_TO_CART_MODAL,
              });
            }
          } catch (e) {
            console.error('Failed on handleWidgetPluginAdded Event', e);
          }
        }

        async function openManageStaff() {
          await editorSdkAdapter.openStaffList('editor');
          editorSdkAdapter.refreshApp('MANAGE_BOOKINGS_CLOSE');
        }

        function focusOnStateBox(widgetComponentRef) {
          getStateBoxByBookingsAppBuilderWidget(
            editorSDK,
            appToken,
            widgetComponentRef,
          ).then((stateBoxComponentRef) => {
            editorSdkAdapter.selectComponent(stateBoxComponentRef);
          });
        }

        async function openDailyTimeTableSettingsPanel(
          componentRef,
          tab?: 'text',
        ) {
          const innerPath = tab ? `/${tab}` : ``;

          const url = `https://bookings.wixapps.net/bookings-app-builder-statics/daily-timetable/settings${innerPath}`;
          const title = editorTranslation.t(
            'bookings.daily-timetable.settings.label.Header',
          );
          const helpId = 'b78664ce-6970-4eb8-9ee6-6ce1d79d9b61';
          const options = {
            width: 406,
          };
          await editorSdkAdapter.openSettingsPanel(
            title,
            url,
            componentRef,
            helpId,
            instance,
            options,
          );
        }

        async function openStaffListSettingsPanel(componentRef, tab = '') {
          const url = `https://bookings.wixapps.net/bookings-app-builder-statics/staff-widget/settings/${tab}`;
          const title = editorTranslation.t(
            'bookings.staff-list.settings.label.Header',
          );
          const helpId = '8a5134e7-7689-4772-acc4-fb2a9830b54e';
          const options = {
            width: 406,
          };
          await editorSdkAdapter.openSettingsPanel(
            title,
            url,
            componentRef,
            helpId,
            instance,
            options,
          );
        }

        async function openBookButtonSettingsPanel(componentRef) {
          const url = `${BookingsAppBuilderStaticsUrls.BOOK_BUTTON_SETTINGS}`;
          const title = editorTranslation.t(
            'bookings.book-button.settings.label.Header',
          );
          const helpId = '0b25d4f6-6381-4d40-ac59-97a7d14cc7c2';
          await editorSdkAdapter.openSettingsPanel(
            title,
            url,
            componentRef,
            helpId,
            instance,
          );
        }

        async function handleComponentStyleChange() {
          const styledComponentRef = eventPayload.compRef;
          const linkedBookingsWidget = await getParentWidget(
            styledComponentRef,
          );
          if (linkedBookingsWidget) {
            const { widgetComponentRef, editorComponentModel } =
              linkedBookingsWidget;
            await editorComponentModel.handleStylesChange(
              widgetComponentRef,
              styledComponentRef,
            );
          }
        }

        async function getParentWidget(componentRef) {
          const controller =
            await editorSDK.components.refComponents.getHostComponentRef(
              appToken,
              {
                componentRef,
              },
            );
          const controllerId = await editorSdkAdapter.getWidgetId(controller);

          if (bookingsEditorComponentModels[controllerId]) {
            return {
              widgetComponentRef: controller,
              editorComponentModel: bookingsEditorComponentModels[controllerId],
            };
          }
          return null;
        }

        async function getControllerWidget(childComponentRef) {
          const hostWidget =
            await editorSDK.components.refComponents.getHostComponentRef(
              appToken,
              {
                componentRef: childComponentRef,
              },
            );
          const ancestors = await editorSDK.components.getAncestors(appToken, {
            componentRef: childComponentRef,
          });
          const widgets = ancestors.find(({ id }) => id === hostWidget?.id)
            ? ancestors
            : [...ancestors, hostWidget];
          for (const widget of widgets) {
            const data = await editorSDK.components.data.get(appToken, {
              componentRef: widget,
            });
            if (data?.type === 'AppController') {
              return widget;
            }
          }
          return null;
        }

        async function handleDoubleClick() {
          let parentWidget;
          try {
            parentWidget = await getParentWidget(eventPayload.componentRef);
          } catch (e) {
            // todo try for book-button
          }
          if (
            parentWidget?.editorComponentModel.baseComponentModel
              .doubleClickAction
          ) {
            eventPayload.id =
              parentWidget.editorComponentModel.baseComponentModel.doubleClickAction.actionId;
          }
        }

        try {
          switch (eventType) {
            case EditorEventTypes.EDIT_FORM_FIELDS:
              void openFormFieldSettingsPanel(editorSDK, appToken);
              break;
            case EditorEventTypes.SET_YOUR_AVAILABILITY:
              void openBackOfficeCalendar(editorSDK, appToken);
              break;
            case EventType.widgetPluginShowOnPageClicked:
              void handleWidgetPluginAdded();
              break;
            case EventType.widgetPluginAdded:
              void handleWidgetPluginAdded();
              break;
            case EditorEventTypes.APP_ACTION_CLICKED:
              if (eventPayload.actionId === EditorActionEvents.OPEN_CALENDAR) {
                reportEditorAction({
                  appId: BOOKINGS_APP_DEF_ID,
                  component: BI_COMPONENT_APP_ACTION,
                  element: 'calendar',
                });
                void openBackOfficeCalendar(editorSDK, appToken);
              }
              if (
                eventPayload.actionId === EditorActionEvents.MANAGE_CLIENT_FLOW
              ) {
                reportEditorAction({
                  appId: BOOKINGS_APP_DEF_ID,
                  component: BI_COMPONENT_APP_ACTION,
                  element: 'manage client flow',
                });
                void openManageClientFlow(editorSDK, appToken);
              }
              if (
                eventPayload.actionId === EditorActionEvents.OPEN_SHARABLE_LINKS
              ) {
                reportEditorAction({
                  appId: BOOKINGS_APP_DEF_ID,
                  component: BI_COMPONENT_APP_ACTION,
                  element: 'sharable links',
                });
                void openSharableLinks(editorSDK, appToken);
              }
              if (
                eventPayload.actionId === EditorActionEvents.OPEN_INTEGRATIONS
              ) {
                reportEditorAction({
                  appId: BOOKINGS_APP_DEF_ID,
                  component: BI_COMPONENT_APP_ACTION,
                  element: 'integrations',
                });
                void openIntegrations(editorSDK, appToken);
              }
              if (
                eventPayload.actionId ===
                EditorActionEvents.OPEN_BOOKINGS_SETTINGS
              ) {
                reportEditorAction({
                  appId: BOOKINGS_APP_DEF_ID,
                  component: BI_COMPONENT_APP_ACTION,
                  element: 'bookings settings',
                });
                void openBookingsSettings(editorSDK, appToken);
              }
              if (
                eventPayload.actionId === EditorActionEvents.MANAGE_BOOKINGS
              ) {
                void openManageBookings(editorSdkAdapter, editorSDK, appToken);
              } else if (
                eventPayload.actionId ===
                EditorActionEvents.OPEN_BOOKINGS_ADD_PANEL
              ) {
                void handleOpenBookingsAddPanel(editorSDK, appToken);
              } else if (
                eventPayload.actionId ===
                EditorActionEvents.OPEN_BOOKINGS_PAGES_PANEL
              ) {
                void handleOpenBookingsPagesPanel(editorSDK, appToken);
              } else if (
                eventPayload.actionId === EditorActionEvents.CREATE_A_SERVICE
              ) {
                void openBookingsDashboardAddNewService(
                  editorSdkAdapter,
                  editorSDK,
                  appToken,
                );
              } else if (
                eventPayload.actionId === EditorActionEvents.MANAGE_STAFF
              ) {
                reportEditorAction({
                  appId: BOOKINGS_APP_DEF_ID,
                  component: BI_COMPONENT_APP_ACTION,
                  element: 'manage staff',
                });
                void openManageStaff();
              } else if (
                eventPayload.actionId ===
                EditorActionEvents.OPEN_MANAGE_AVAILABILITY
              ) {
                reportEditorAction({
                  appId: BOOKINGS_APP_DEF_ID,
                  component: BI_COMPONENT_APP_ACTION,
                  element: 'manage availability',
                });
                void openBookingsAvailability(editorSDK, appToken);
              }
              break;
            case EditorEventTypes.COMPONENT_STYLE_CHANGED:
              void handleComponentStyleChange();
              break;
            case EditorEventTypes.WIDGET_GFPP_CLICKED:
              if (
                eventPayload.gfppOrigin ===
                EditorEventTypes.GFPP_ORIGIN_DOUBLE_CLICK
              ) {
                await handleDoubleClick();
              }
              if (eventPayload?.id === EditorActionEvents.MANAGE_BOOKINGS) {
                void openManageBookings(editorSdkAdapter, editorSDK, appToken);
              }
              if (eventPayload?.id === EditorActionEvents.MANAGE_STATE) {
                focusOnStateBox(eventPayload.componentRef);
              }
              if (
                eventPayload?.id ===
                EditorActionEvents.OPEN_BOOK_BUTTON_SETTINGS
              ) {
                void openBookButtonSettingsPanel(eventPayload.componentRef);
              }
              if (
                eventPayload?.id ===
                EditorActionEvents.OPEN_DAILY_TIME_TABLE_SETTINGS
              ) {
                void openDailyTimeTableSettingsPanel(eventPayload.componentRef);
              }
              if (
                eventPayload?.id === EditorActionEvents.OPEN_STAFF_LIST_SETTINGS
              ) {
                void openStaffListSettingsPanel(eventPayload.componentRef);
              }
              if (eventPayload?.id === EditorActionEvents.MANAGE_STAFF) {
                void openManageStaff();
              }
              break;
            case EditorEventTypes.COMPONENT_GFPP_CLICKED:
              const parentWidget = await getControllerWidget(
                eventPayload.componentRef,
              );
              if (eventPayload?.id === EditorActionEvents.MANAGE_BOOKINGS) {
                void openManageBookings(editorSdkAdapter, editorSDK, appToken);
              }
              if (
                eventPayload?.id === EditorActionEvents.OPEN_STAFF_LIST_SETTINGS
              ) {
                void openStaffListSettingsPanel(parentWidget);
              }
              if (eventPayload?.id === EditorActionEvents.MANAGE_STAFF) {
                void openManageStaff();
              }
              if (
                eventPayload?.id ===
                EditorActionEvents.OPEN_DAILY_TIME_TABLE_SETTINGS_TEXT_TAB
              ) {
                void openDailyTimeTableSettingsPanel(parentWidget, 'text');
              }
              if (
                eventPayload?.id ===
                EditorActionEvents.OPEN_DAILY_TIME_TABLE_SETTINGS
              ) {
                void openDailyTimeTableSettingsPanel(parentWidget);
              }
              if (
                eventPayload?.id ===
                EditorActionEvents.OPEN_STAFF_LIST_TEXT_SETTINGS
              ) {
                void openStaffListSettingsPanel(parentWidget, 'text');
              }
              if (eventPayload?.id === EditorActionEvents.MANAGE_STATE) {
                focusOnStateBox(parentWidget);
              }
              break;
            case EditorActionEvents.MANAGE_BOOKINGS:
              reportEditorAction({
                appId: BOOKINGS_APP_DEF_ID,
                component: 'icons section',
                element: eventPayload?.id || eventType,
              });
              void openManageBookings(editorSdkAdapter, editorSDK, appToken);
              break;
            case EditorEventTypes.DELETE_BOOKINGS:
              const bookingsPages = await getAllBookingsPages(
                editorSDK,
                appToken,
              );
              const essentialPage = bookingsPages.find(
                (page) => page.tpaPageId === PageId.BOOKINGS_LIST,
              );
              await removePage(editorSDK, appToken, essentialPage.id);
              break;
            case EditorEventTypes.DELETE_PAGE:
              await removePage(editorSDK, appToken, eventPayload.pageRef.id);
              break;
            case EditorEventTypes.PAGE_CHANGED:
              const pageData = await getPageData(
                sdk,
                appToken,
                eventPayload.pageRef,
              );
              if (
                pageData.tpaPageId === PageId.SCHEDULER &&
                isProposeMigrationNeeded &&
                !(await isBookingsCheckoutInstalled(editorSDK))
              ) {
                await proposeListMigration(
                  sdk,
                  appToken,
                  locale,
                  instance,
                  editorTranslation,
                  allSitePages,
                );
              } else {
                if (
                  pageData.tpaPageId === PageId.BOOKINGS_LIST &&
                  !isAdi &&
                  (!(await isServicePageInstalled(sdk)) ||
                    !(await isBookingCalendarInstalled(sdk)) ||
                    !(await isBookingFormInstalled(sdk)))
                ) {
                  await migrator({
                    sdk,
                    appToken,
                    locale,
                    instance,
                    editorTranslation,
                    allSitePages,
                    editorScriptApi,
                    referral: pageData.tpaPageId,
                  });
                }
              }
              break;
            default:
          }
          fedopsLogger.interactionEnded(Actions.ON_EVENT);
        } catch (e) {
          const errorMessage = `${
            e?.message ? e.message : JSON.stringify(e)
          } - Event Type - ${eventType} - Action ${Actions.ON_EVENT}`;
          reportBi({
            msid,
            origin: editorOrigin,
            isFirstInstall,
            actionType: Actions.ON_EVENT,
            actionSucceed: false,
            errorMessage,
          });
          throw new Error(errorMessage);
        }
      },
      handleAction: async (
        { type, payload }: { type: string; payload: HandleActionPayload },
        editorSdk,
      ) => {
        try {
          fedopsLogger.interactionStarted(Actions.HANDLE_ACTION);

          const editorSdkAdapter = new EditorSdkAdapter(editorSdk, appToken);

          switch (type) {
            case 'appInstalled':
              fedopsLogger.interactionStarted(Actions.APP_INSTALLED);
              try {
                if (payload.appDefinitionId === ECOM_APP_DEF_ID) {
                  fedopsLogger.interactionEnded(Actions.ECOM_INSTALLATION);
                  if (isBookingsOnEcom && isFirstInstall) {
                    await removeBookCheckoutPageOnEcom(editorSdk, appToken);
                  }
                }
                if (payload.appDefinitionId === BOOKINGS_APP_DEF_ID) {
                  fedopsLogger.interactionEnded(Actions.BOOKINGS_INSTALLATION);
                  if (
                    isBookingsOnEcom &&
                    isFirstInstall &&
                    (await editorSdkAdapter.isEcomInstalled())
                  ) {
                    await removeBookCheckoutPageOnEcom(editorSdk, appToken);
                  }
                }
                fedopsLogger.interactionEnded(Actions.APP_INSTALLED);
              } catch (e) {
                const errorMessage = `${
                  e?.message ? e.message : JSON.stringify(e)
                } - Action ${Actions.APP_INSTALLED}`;
                reportBi({
                  msid,
                  origin: editorOrigin,
                  isFirstInstall,
                  actionType: Actions.APP_INSTALLED,
                  actionSucceed: false,
                  errorMessage,
                });
                throw new Error(errorMessage);
              }
              break;
            case 'migrate':
              try {
                fedopsLogger.interactionStarted(Actions.MIGRATE);
                await migrateAction({
                  editorSdk,
                  appToken,
                  editorScriptApi,
                  editorOptions,
                  instance,
                  payload,
                  isBookingsOnEcom,
                });
                fedopsLogger.interactionEnded(Actions.MIGRATE);
              } catch (e) {
                const errorMessage = `${
                  e?.message ? e.message : JSON.stringify(e)
                } - Action ${Actions.MIGRATE}`;
                reportBi({
                  msid,
                  origin: editorOrigin,
                  isFirstInstall,
                  actionType: Actions.MIGRATE,
                  actionSucceed: false,
                  errorMessage,
                });
                throw new Error(errorMessage);
              }
              break;
            case 'removeApp':
              try {
                fedopsLogger.interactionStarted(Actions.APP_REMOVED);
                await onRemoveApp(sdk, appToken);
                fedopsLogger.interactionEnded(Actions.APP_REMOVED);
              } catch (e) {
                const errorMessage = `${
                  e?.message ? e.message : JSON.stringify(e)
                } - Action ${Actions.APP_REMOVED}`;
                reportBi({
                  msid,
                  origin: editorOrigin,
                  isFirstInstall,
                  actionType: Actions.APP_REMOVED,
                  actionSucceed: false,
                  errorMessage,
                });
                throw new Error(errorMessage);
              }
              break;
            default:
              return Promise.resolve();
          }
          fedopsLogger.interactionEnded(Actions.HANDLE_ACTION);
        } catch (e) {
          const errorMessage = `${
            e?.message ? e.message : JSON.stringify(e)
          } - Action ${Actions.HANDLE_ACTION}`;
          reportBi({
            msid,
            origin: editorOrigin,
            isFirstInstall,
            actionType: Actions.HANDLE_ACTION,
            actionSucceed: false,
            errorMessage,
          });
          throw new Error(errorMessage);
        }
      },
      getControllerPresets: async () => [],
      exports: () => ({ editor: {} }), // add dummy exports for ecom wrapping
    };
  } catch (e) {
    const errorMessage = `${
      e?.message ? e.message : JSON.stringify(e)
    } - Action: ${Actions.CREATE_EDITOR_SCRIPT}`;
    reportBi({
      actionType: Actions.CREATE_EDITOR_SCRIPT,
      actionSucceed: false,
      errorMessage,
    });
    throw new Error(errorMessage);
  }
};

export const createBookingsEditorScriptWithMembers = (
  editorTranslation: Translations,
  isBookingsOnEcom = false,
  baseEditorScript?: any,
) => {
  try {
    const isEditor3 = globalThis.commonConfig?.host === 'EDITOR3';
    const myBookingsAppId = isEditor3
      ? MA_APP_IDS.MY_BOOKINGS_PAGE
      : MA_APP_IDS.MY_BOOKINGS;

    return withMembersArea(
      baseEditorScript ??
        createEditorScript(editorTranslation, isBookingsOnEcom),
      {
        installAutomatically: false,
        membersAreaApps: [myBookingsAppId, MA_APP_IDS.MY_WALLET],
      },
    );
  } catch (e) {
    const errorMessage = `${
      e?.message ? e.message : JSON.stringify(e)
    } - errorCode: WITH_MEMBER_AREA_EDITOR_SCRIPT_ERROR`;
    throw new Error(errorMessage);
  }
};

export const createBookingsEditorScriptWithEcom = () => {
  try {
    const editorTranslation = new Translations();
    const editorScript = withEcomPlatformData(
      createEditorScript(editorTranslation, true) as any,
      () => requiredEcomComponents(),
    );

    const originalExports = editorScript.exports;

    editorScript.exports = (...args) => {
      const result =
        typeof originalExports === 'function'
          ? originalExports(...args)
          : originalExports ?? {};

      if (!result.editor) {
        result.editor = {};
      }

      (result.editor as any).getDynamicPageNavBarConfig = async ({
        numberOfItems,
      }) => {
        try {
          await editorTranslation.waitForInit();
          return {
            navBarMainAction: {
              label: editorTranslation.t('page-navigation.manage-services.cta'),
              actionId: EditorActions.openManageServices,
            },
            numberAndLabel:
              numberOfItems === 1
                ? editorTranslation.t('page-navigation.number-and-label.one')
                : editorTranslation.t('page-navigation.number-and-label.many', {
                    count: numberOfItems,
                  }),
          };
        } catch (e) {
          console.error('Error in getDynamicPageNavBarConfig', e);
          return {
            navBarMainAction: {
              label: 'Manage Services',
              actionId: EditorActions.openManageServices,
            },
            numberAndLabel: `${numberOfItems} services`,
          };
        }
      };

      return result;
    };

    return createBookingsEditorScriptWithMembers(
      editorTranslation,
      true,
      editorScript,
    );
  } catch (e) {
    const errorMessage = `${
      e?.message ? e.message : JSON.stringify(e)
    } - errorCode: WITH_ECOM_PLATFORM_EDITOR_SCRIPT_ERROR`;
    throw new Error(errorMessage);
  }
};
